import type { GetFinalizedStoryResponse, GetStoryResponse, Story, StoryStepWithDrawing } from "@api/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BookOpen, Eye, Palette, RotateCcw, Trash2, Zap } from "lucide-react";
import React, { lazy, Suspense, useState } from "react";
import { useNavigation } from "../contexts/NavigationContext";
import { useVoice } from "../contexts/VoiceContext";
import { useToast } from "../hooks/useToast";
import { apiClient } from "../lib/api";
import { getErrorInfo, type ApiError } from "../lib/errorUtils";
import { capitalize } from "../lib/utils";
import { clearDrawingDraft } from "../utils/drawingDraft";
import type { CompressedImages } from "../utils/imageCompression";
import { PageLayout } from "./PageLayout";
import { BrandedSpinner } from "./ui/branded-spinner";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { ConditionalVoiceButton } from "./ui/conditional-voice-button";
import { ConfirmationModal } from "./ui/confirmation-modal";
import { Spinner } from "./ui/spinner";
import { StoryText } from "./ui/story-text";

const DrawingCanvas = lazy(() => import("./DrawingCanvas"));

function isStoryFinalized(storyData: { story?: { status?: string } }): storyData is GetFinalizedStoryResponse {
    return storyData?.story?.status === "finalized";
}

function useStoryState(story: Story | undefined, currentStep: StoryStepWithDrawing | undefined) {
    const queryClient = useQueryClient();
    const [canvasKey, setCanvasKey] = useState(story?.current_step || 0);
    const [submittedDrawing, setSubmittedDrawing] = useState<string | null>(null);
    const [drawingResult, setDrawingResult] = useState<{
        interpretation: string;
        followupText?: string;
    } | null>(null);

    // Initialize state based on drawing data from API
    React.useEffect(() => {
        if (currentStep?.drawing) {
            // Step has drawing submission - show it
            setSubmittedDrawing(currentStep.drawing.image_data);
            setDrawingResult({
                interpretation: currentStep.drawing.ai_interpretation,
                followupText: currentStep.story_followup_text,
            });
        } else {
            // No drawing yet - show canvas
            setSubmittedDrawing(null);
            setDrawingResult(null);
        }
    }, [currentStep?.id, currentStep?.drawing, currentStep?.story_followup_text]);

    // Draw Again: Only reset UI state, keep same canvas instance
    const handleDrawAgain = () => {
        setSubmittedDrawing(null);
        setDrawingResult(null);
        // canvasKey unchanged = same DrawingCanvas instance = preserved state
    };

    // Continue to next step: Force new canvas instance + reset UI state
    const handleContinue = (currentStepId?: string) => {
        // Clear the current step's draft before progressing
        if (story?.id && currentStepId) {
            clearDrawingDraft(story.id, currentStepId);
        }

        setCanvasKey((prev) => prev + 1); // Force remount with new key
        setSubmittedDrawing(null);
        setDrawingResult(null);
    };

    const refreshStory = () => {
        queryClient.invalidateQueries({ queryKey: ["story", story?.id] });
    };

    return {
        canvasKey,
        submittedDrawing,
        drawingResult,
        setSubmittedDrawing,
        setDrawingResult,
        handleDrawAgain,
        handleContinue,
        refreshStory,
    };
}

function useStoryMutations(story: Story | undefined, storyState: ReturnType<typeof useStoryState>) {
    const { navigateToMyStories } = useNavigation();
    const queryClient = useQueryClient();
    const { handleContinue, refreshStory } = storyState;
    const { toast } = useToast();

    const drawingMutation = useMutation({
        mutationFn: (compressedImages: CompressedImages) => {
            if (!story?.id || !story?.current_step) {
                throw new Error("Missing story data");
            }
            return apiClient.submitStoryDrawing(
                story.id,
                story.current_step,
                compressedImages.display,
                compressedImages.compressed,
                compressedImages.sketchingTimeMs
            );
        },
        onSuccess: (result) => {
            // Invalidate quota status to refresh usage after drawing submission
            queryClient.invalidateQueries({ queryKey: ["quota-status"] });

            // Set the drawing result in UI state
            storyState.setDrawingResult({
                interpretation: result.submission.ai_interpretation || "Unknown",
                followupText: result.followupText,
            });

            // Refresh story data to get the updated step with drawing data
            refreshStory();
        },
        onError: (error: ApiError) => {
            const errorInfo = getErrorInfo(error);

            // Show quota-specific error messages
            if (errorInfo.type === "quota_exceeded") {
                toast({
                    title: errorInfo.title,
                    description: errorInfo.message,
                    variant: "destructive",
                });
                // Refresh quota status to show updated limits
                queryClient.invalidateQueries({ queryKey: ["quota-status"] });
            } else {
                toast({
                    title: "Drawing Submission Failed",
                    description: errorInfo.message,
                    variant: "destructive",
                });
            }

            storyState.setDrawingResult({
                interpretation: "Error processing drawing",
            });
        },
    });

    const progressMutation = useMutation({
        mutationFn: (_stepId?: string) => {
            if (!story?.id) {
                throw new Error("Missing story data");
            }
            return apiClient.progressToNextStep(story.id);
        },
        onSuccess: (_, stepId) => {
            handleContinue(stepId);
            refreshStory();

            // Scroll to top after new content loads
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
            }, 100);
        },
    });

    const deleteStoryMutation = useMutation({
        mutationFn: () => {
            if (!story?.id) {
                throw new Error("Missing story data");
            }
            return apiClient.deleteStory(story.id);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["user-stories-paginated"] });
            navigateToMyStories();
        },
        onError: () => {
            alert("Failed to delete story. Please try again.");
        },
    });

    return { drawingMutation, progressMutation, deleteStoryMutation };
}

function DrawingPhase({
    currentStep,
    canvasKey,
    onSubmit,
    isSubmitting,
    storyId,
    stepId,
    stepNumber,
}: {
    currentStep: { drawing_prompt?: string; user_prompt?: string };
    canvasKey: number;
    onSubmit: (compressedImages: CompressedImages) => void;
    isSubmitting: boolean;
    storyId: string;
    stepId: string;
    stepNumber: number;
}) {
    // Use user_prompt for instructions, fallback to drawing_prompt for backward compatibility
    const userPrompt = currentStep.user_prompt || `Draw ${currentStep.drawing_prompt || "something"}`;
    const drawingPrompt = capitalize(currentStep.drawing_prompt || "");

    return (
        <>
            {/* Enhanced Drawing Prompt Callout */}
            <div className="drawing-prompt-callout bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-2 text-secondary">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center">
                        <Palette className="h-4 w-4" />
                    </div>
                    <span className="text-md font-medium">Your Turn to Draw</span>
                </div>

                <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg md:text-xl font-semibold flex-1">{userPrompt}</h3>
                    {userPrompt && (
                        <ConditionalVoiceButton
                            text={userPrompt}
                            variant="outline"
                            size="sm"
                            className="flex-shrink-0"
                        />
                    )}
                </div>

                {drawingPrompt && <div className="text-center text-2xl font-bold text-secondary">{drawingPrompt}</div>}
            </div>

            <Suspense fallback={<BrandedSpinner size="lg" animation="pulse" />}>
                <DrawingCanvas
                    key={canvasKey}
                    onSubmit={onSubmit}
                    isSubmitting={isSubmitting}
                    className="canvas-panel rounded-lg p-4"
                    storyId={storyId}
                    stepId={stepId}
                    stepNumber={stepNumber}
                />
            </Suspense>
        </>
    );
}

function DrawingAnalysis({
    drawingResult,
    isAnalyzing,
    onContinue,
    isProgressing,
    isOnFinalStep,
}: {
    drawingResult: {
        interpretation: string;
        followupText?: string;
    } | null;
    isAnalyzing: boolean;
    onContinue?: () => void;
    isProgressing?: boolean;
    isOnFinalStep?: boolean;
}) {
    if (isAnalyzing) {
        return (
            <div className="ai-analysis-loading space-y-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <Zap className="absolute inset-0 m-auto h-3 w-3 text-primary" />
                    </div>
                    <div>
                        <p className="font-medium text-primary">Looking at your drawing...</p>
                        <p className="text-sm text-muted-foreground">This usually takes a few seconds</p>
                    </div>
                </div>

                {/* Skeleton loading content */}
                <div className="space-y-3">
                    <div className="h-3 bg-muted rounded animate-pulse w-3/4" />
                    <div className="h-3 bg-muted rounded animate-pulse w-1/2" />
                    <div className="h-3 bg-muted rounded animate-pulse w-2/3" />
                </div>
            </div>
        );
    }

    if (!drawingResult) return null;

    return (
        <div className="ai-analysis bg-muted/30 rounded-lg px-4">
            <div className="flex items-center gap-2 mb-3">
                <h3 className="text-lg font-semibold flex items-center gap-2 text-primary">
                    <Eye className="h-4 w-4" />
                    What I See
                </h3>
            </div>

            <div className="space-y-3">
                <div className="flex items-start gap-2">
                    <p className="text-md leading-relaxed flex-1">
                        <span className="font-semibold">{capitalize(drawingResult.interpretation)}</span>
                    </p>
                    <ConditionalVoiceButton
                        text={capitalize(drawingResult.interpretation)}
                        variant="ghost"
                        className="flex-shrink-0"
                    />
                </div>

                {drawingResult.followupText && (
                    <div className="pt-2 border-t border-muted">
                        <div className="text-sm leading-relaxed text-muted-foreground">
                            <StoryText>{drawingResult.followupText}</StoryText>
                        </div>
                    </div>
                )}

                {onContinue && (
                    <div className="pt-4 flex justify-center">
                        {isOnFinalStep ? (
                            <Button
                                onClick={onContinue}
                                size="sm"
                                className="flex items-center gap-2"
                                disabled={isProgressing}
                            >
                                {isProgressing && <Spinner size="sm" />}
                                {isProgressing ? "Finishing..." : "View Complete Story"}
                            </Button>
                        ) : (
                            <Button
                                onClick={onContinue}
                                size="sm"
                                className="flex items-center gap-2"
                                disabled={isProgressing}
                            >
                                {isProgressing && <Spinner size="sm" />}
                                {isProgressing ? "Creating..." : "Continue to Next Chapter"}
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function DrawingResultPhase({
    currentStep,
    submittedDrawing,
    drawingResult,
    isAnalyzing,
    onDrawAgain,
    onContinue,
    isProgressing,
    isOnFinalStep,
}: {
    currentStep: { drawing_prompt?: string; user_prompt?: string };
    submittedDrawing: string;
    drawingResult: {
        interpretation: string;
        followupText?: string;
    } | null;
    isAnalyzing: boolean;
    onDrawAgain: () => void;
    onContinue: () => void;
    isProgressing: boolean;
    isOnFinalStep: boolean;
}) {
    // Use user_prompt for display, fallback to drawing_prompt for backward compatibility
    const displayPrompt = currentStep.user_prompt || `Draw ${currentStep.drawing_prompt || "something"}`;

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="flex-shrink-0 w-fit">
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold flex items-center gap-2 text-secondary">
                            <Palette className="h-4 w-4" />
                            Your Drawing
                        </h3>
                        <p className="text-sm text-muted-foreground w-full md:max-w-66">"{displayPrompt}"</p>
                        <img
                            src={submittedDrawing}
                            alt="Your drawing"
                            className="w-full md:w-66 h-36 object-contain rounded-lg bg-background"
                        />
                        {!isAnalyzing && (
                            <div className="flex justify-center">
                                <Button onClick={onDrawAgain} variant="outline" size="sm" className="w-fit">
                                    <RotateCcw className="h-4 w-4 mr-2" />
                                    Draw Again
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex-1 min-w-0 w-full">
                    <DrawingAnalysis
                        drawingResult={drawingResult}
                        isAnalyzing={isAnalyzing}
                        onContinue={drawingResult ? onContinue : undefined}
                        isProgressing={isProgressing}
                        isOnFinalStep={isOnFinalStep}
                    />
                </div>
            </div>
        </div>
    );
}

function StoryProgress({ story }: { story: Story }) {
    return (
        <div className="progress-section space-y-3">
            <div className="flex items-baseline gap-4">
                <span className="text-lg font-medium">Progress:</span>
                <span className="text-sm text-muted-foreground">
                    Chapter {story.current_step}/{story.total_steps}
                </span>
            </div>

            <div className="flex gap-2">
                {Array.from({ length: story.total_steps || 0 }).map((_, i) => {
                    const isCompleted = i < (story.current_step || 1) - 1;
                    const isCurrent = i === (story.current_step || 1) - 1;

                    return (
                        <div
                            key={i}
                            className={`flex-1 h-2 rounded-full transition-all border-2 ${
                                isCompleted
                                    ? "bg-success border-success"
                                    : isCurrent
                                      ? "bg-primary border-primary"
                                      : "bg-muted border-secondary"
                            }`}
                        />
                    );
                })}
            </div>
        </div>
    );
}

function StoryHeader({ story }: { story: Story | undefined }) {
    return <div className="text-center space-y-6 mb-8">{story && <StoryProgress story={story} />}</div>;
}

export function StoryEditor({ storyData }: { storyData: GetStoryResponse | GetFinalizedStoryResponse }) {
    const { stop: stopVoice } = useVoice();
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    const isFinalized = storyData ? isStoryFinalized(storyData) : false;
    const story = storyData?.story;
    const currentStep = storyData?.steps?.find((step) => step.step_number === story?.current_step);

    const storyState = useStoryState(story, currentStep);
    const { drawingMutation, progressMutation, deleteStoryMutation } = useStoryMutations(story, storyState);

    const handleDrawingSubmit = (compressedImages: CompressedImages) => {
        storyState.setSubmittedDrawing(compressedImages.display);
        drawingMutation.mutate(compressedImages);
    };

    const handleContinueStory = () => {
        // Stop any playing audio when continuing to avoid confusion
        stopVoice();

        progressMutation.mutate(currentStep?.id);
    };

    const handleDeleteClick = () => setDeleteModalOpen(true);
    const handleDeleteConfirm = () => deleteStoryMutation.mutate();
    const handleDeleteCancel = () => setDeleteModalOpen(false);

    // If story is finalized, show message
    if (isFinalized) {
        return (
            <div className="text-center py-8">
                <BookOpen className="h-12 w-12 text-primary mx-auto mb-4" />
                <h2 className="text-2xl font-bold">Story Complete!</h2>
                <p className="text-muted-foreground">This story is finished. It should be in view mode.</p>
            </div>
        );
    }

    const pageTitle = "Story Editor";

    return (
        <PageLayout
            title={pageTitle}
            breadcrumbs={[
                { label: "Home", to: "/" },
                { label: "My Stories", to: "/my-stories" },
                { label: "Edit Story" },
            ]}
        >
            <main className="container mx-auto px-4 sm:px-6 relative">
                <div className="max-w-4xl mx-auto">
                    {/* Absolute positioned delete button */}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleDeleteClick}
                        className="absolute top-[52px] sm:top-0 right-2 sm:right-6 h-8 w-8 p-0 text-destructive hover:text-destructive/80 hover:bg-destructive/10 dark:hover:bg-destructive/20"
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>

                    <div className="mb-6">
                        <h1 className="text-2xl sm:text-3xl font-bold text-primary text-center">
                            {story?.title || story?.topic || "Your Story"}
                        </h1>
                    </div>

                    <StoryHeader story={story} />

                    <div className="space-y-8">
                        {currentStep ? (
                            <>
                                <Card className="p-4 md:p-8">
                                    <div className="space-y-6">
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                                <BookOpen className="h-4 w-4 text-primary" />
                                            </div>
                                            <h3 className="font-semibold text-primary">Your Story</h3>
                                        </div>

                                        {/* Break up dense paragraphs and improve readability */}
                                        <div className="story-content space-y-4">
                                            <div className="prose prose-lg md:prose-xl max-w-none">
                                                {currentStep.story_text.split("\n\n").map((paragraph, index) => (
                                                    <div key={index} className="mb-4">
                                                        <StoryText>{paragraph}</StoryText>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </Card>

                                <div className="space-y-6">
                                    {!storyState.submittedDrawing ? (
                                        <DrawingPhase
                                            currentStep={currentStep}
                                            canvasKey={storyState.canvasKey}
                                            onSubmit={handleDrawingSubmit}
                                            isSubmitting={drawingMutation.isPending}
                                            storyId={storyData.story.id}
                                            stepId={currentStep.id}
                                            stepNumber={currentStep.step_number}
                                        />
                                    ) : (
                                        <DrawingResultPhase
                                            currentStep={currentStep}
                                            submittedDrawing={storyState.submittedDrawing}
                                            drawingResult={storyState.drawingResult}
                                            isAnalyzing={drawingMutation.isPending}
                                            onDrawAgain={storyState.handleDrawAgain}
                                            onContinue={handleContinueStory}
                                            isProgressing={progressMutation.isPending}
                                            isOnFinalStep={story ? story.current_step >= story.total_steps : false}
                                        />
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-8">
                                <p className="text-muted-foreground">No current step found for this story.</p>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            <ConfirmationModal
                isOpen={deleteModalOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title="Delete Story"
                description={`Are you sure you want to delete "${story?.topic}"? This will permanently remove all your progress and drawings. This action cannot be undone.`}
                variant="destructive"
                confirmButtonText="Delete"
                isLoading={deleteStoryMutation.isPending}
                loadingText="Deleting..."
            />
        </PageLayout>
    );
}
